/**
 * BatchAnalytiquePanel — Panneau analytique pour un batch individuel.
 * Affiché en onglet "Analytique" dans chaque vue de batch finalisé.
 * Props :
 *   batch    : objet batch complet (id, produit, nom, ...)
 *   onFermer : (optionnel) callback pour fermer le panneau dans un modal
 */

import { useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { api } from '../hooks/useApi';
import Icon from './Icon';
import './BatchAnalytiquePanel.css';

// ─── Config prix vente par produit ───────────────────────────────────────────

const PRIX_VENTE_DEFAUT = {
  saucisses:    16.00,
  porc:         16.00,
  brisket:      28.00,
  burnt_ends:   27.00,
  cotes_levees: 0,
  saumon:       0,
  sauce:        12.00,
};

const COULEURS_PIE = ['#c8941a', '#e07b39', '#7c5cbf', '#2563eb', '#16a34a', '#0891b2', '#c0392b'];

const fmt$ = (v) => `${(+v || 0).toFixed(2)} $`;
const fmtPct = (v) => `${(+v || 0).toFixed(1)} %`;

// ─── Calcul coût matières par produit ────────────────────────────────────────

function calculerCoutMatieres(batch) {
  if (batch.produit === 'saucisses') {
    const porc   = ((batch.poids_porc_kg  || 0) * (batch.prix_kg_porc  || 0));
    const boeuf  = ((batch.poids_boeuf_kg || 0) * (batch.prix_kg_boeuf || 0));
    const gras   = ((batch.poids_gras_kg  || 0) * (batch.prix_kg_gras  || 0));
    const total  = porc + boeuf + gras;
    return {
      total,
      detail: [
        { nom: 'Epaule de porc',   valeur: porc,  pct: total > 0 ? (porc / total * 100)  : 0 },
        { nom: 'Boeuf wagyu',       valeur: boeuf, pct: total > 0 ? (boeuf / total * 100) : 0 },
        { nom: 'Gras',             valeur: gras,  pct: total > 0 ? (gras / total * 100)  : 0 },
      ].filter(d => d.valeur > 0),
    };
  }
  const poidsCru = batch.poids_cru_kg || batch.poids_cru_total_kg || 0;
  const total    = poidsCru * (batch.prix_kg || 0);
  return {
    total,
    detail: [{ nom: 'Viande / intrants', valeur: total, pct: 100 }],
  };
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function BatchAnalytiquePanel({ batch, onFermer }) {
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    if (!batch?.produit) return;
    api.get(`/analytics/produit/${batch.produit}?nb_batches=6`)
      .then(d => setHistorique(d.historique_batches || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [batch?.produit, batch?.id]);

  if (!batch) return null;

  const { total: coutMat, detail: detailCout } = calculerCoutMatieres(batch);
  const prixVenteDefaut = PRIX_VENTE_DEFAUT[batch.produit] || 0;

  const nbUnites =
    batch.nb_paquets      ||
    batch.nb_sacs         ||
    batch.nb_briskets     ||
    batch.nb_filets       ||
    (batch.nb_pots_250 || 0) + (batch.nb_pots_350 || 0) ||
    0;

  const poidsCru  = batch.poids_cru_kg || batch.poids_cru_total_kg || 0;
  const poidsCuit = batch.poids_apres_cuisson_kg || batch.poids_cuit_kg || 0;
  const coutParUnite = nbUnites > 0 ? Math.round(coutMat / nbUnites * 10000) / 10000 : 0;
  const yieldGlobal  = batch.yield_global_pct || batch.yield_global || 0;

  const batchPrecedents = historique.filter(b => b.id !== batch.id);
  const moyHistorique   = batchPrecedents.length > 0 ? {
    yield  : batchPrecedents.reduce((s, b) => s + (b.yield_global_pct || 0), 0) / batchPrecedents.length,
    marge  : batchPrecedents.reduce((s, b) => s + (b.marge_brute_pct  || 0), 0) / batchPrecedents.length,
    cout   : batchPrecedents.filter(b => b.cout_par_unite > 0).reduce((s, b) => s + b.cout_par_unite, 0)
             / Math.max(batchPrecedents.filter(b => b.cout_par_unite > 0).length, 1),
  } : null;

  const deltaMarge  = moyHistorique ? (batch.marge_brute_pct  || 0) - moyHistorique.marge : null;
  const deltaYield  = moyHistorique ? (yieldGlobal || 0)              - moyHistorique.yield : null;
  const deltaCout   = moyHistorique ? (coutParUnite || 0)             - moyHistorique.cout  : null;

  const dataComparaison = [
    ...batchPrecedents.slice(0, 5).reverse(),
    { ...batch, nom: batch.nom + ' (actuel)', yield_global_pct: yieldGlobal, marge_brute_pct: batch.marge_brute_pct || 0, cout_par_unite: coutParUnite },
  ];

  const suggestions = coutParUnite > 0 ? [
    { marge: 40, prix: coutParUnite / (1 - 0.40) },
    { marge: 45, prix: coutParUnite / (1 - 0.45) },
    { marge: 50, prix: coutParUnite / (1 - 0.50) },
    { marge: 55, prix: coutParUnite / (1 - 0.55) },
  ] : [];

  const margeActuelle = prixVenteDefaut > 0 && coutParUnite > 0
    ? ((prixVenteDefaut - coutParUnite) / prixVenteDefaut) * 100
    : null;

  // ── Skeleton chargement ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bap-container">
        {onFermer && (
          <div className="bap-modal-header">
            <span className="bap-modal-title">Analytique</span>
            <button className="btn btn-ghost btn--sm" onClick={onFermer}>
              <Icon name="fermer" size={14} /> Fermer
            </button>
          </div>
        )}
        <div className="bap-skeleton-grid">
          {[0,1,2,3].map(i => (
            <div key={i} className="skeleton bap-skeleton-card" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bap-container">
      {onFermer && (
        <div className="bap-modal-header">
          <span className="bap-modal-title">Analytique — {batch.nom}</span>
          <button className="btn btn-ghost btn--sm" onClick={onFermer}>
            <Icon name="fermer" size={14} /> Fermer
          </button>
        </div>
      )}

      {/* Performance vs moyenne */}
      <section className="bap-section">
        <h4 className="bap-section-titre">Performance</h4>
        <div className="bap-perf-grid">
          <div className="bap-perf-card card">
            <span className="bap-perf-label">Yield global</span>
            <span className="bap-perf-value">{fmtPct(yieldGlobal)}</span>
            {deltaYield !== null && (
              <span className={`bap-perf-delta ${deltaYield >= 0 ? 'bap-perf-delta--pos' : 'bap-perf-delta--neg'}`}>
                <Icon name={deltaYield >= 0 ? 'succes' : 'erreur'} size={12} />
                {Math.abs(deltaYield).toFixed(1)} % vs moy.
              </span>
            )}
          </div>
          <div className="bap-perf-card card">
            <span className="bap-perf-label">Cout par unite</span>
            <span className="bap-perf-value">{coutParUnite > 0 ? fmt$(coutParUnite) : '—'}</span>
            {deltaCout !== null && coutParUnite > 0 && (
              <span className={`bap-perf-delta ${deltaCout <= 0 ? 'bap-perf-delta--pos' : 'bap-perf-delta--neg'}`}>
                <Icon name={deltaCout <= 0 ? 'succes' : 'erreur'} size={12} />
                {fmt$(Math.abs(deltaCout))} vs moy.
              </span>
            )}
          </div>
          <div className="bap-perf-card card">
            <span className="bap-perf-label">Marge brute</span>
            <span className="bap-perf-value">{margeActuelle !== null ? fmtPct(margeActuelle) : '—'}</span>
            {deltaMarge !== null && margeActuelle !== null && (
              <span className={`bap-perf-delta ${deltaMarge >= 0 ? 'bap-perf-delta--pos' : 'bap-perf-delta--neg'}`}>
                <Icon name={deltaMarge >= 0 ? 'succes' : 'erreur'} size={12} />
                {Math.abs(deltaMarge).toFixed(1)} % vs moy.
              </span>
            )}
          </div>
          <div className="bap-perf-card card">
            <span className="bap-perf-label">Cout matieres total</span>
            <span className="bap-perf-value bap-perf-value--neutral">{fmt$(coutMat)}</span>
            {poidsCru > 0 && (
              <span className="bap-perf-hint">{fmt$(coutMat / poidsCru)} / kg cru</span>
            )}
          </div>
        </div>
      </section>

      {/* Decomposition du cout */}
      {detailCout.length > 0 && coutMat > 0 && (
        <section className="bap-section">
          <h4 className="bap-section-titre">Decomposition du cout matieres</h4>
          <div className="bap-pie-container card">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={detailCout}
                  dataKey="valeur"
                  nameKey="nom"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ nom, pct }) => `${nom.split(' ')[0]} ${pct.toFixed(0)}%`}
                  labelLine={false}
                >
                  {detailCout.map((_, i) => (
                    <Cell key={i} fill={COULEURS_PIE[i % COULEURS_PIE.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 12,
                  }}
                  formatter={(v, name) => [fmt$(v), name]}
                />
                <Legend wrapperStyle={{ color: 'var(--muted)', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="bap-pie-detail">
              {detailCout.map((d, i) => (
                <div key={i} className="bap-pie-row">
                  <span
                    className="bap-pie-swatch"
                    style={{ background: COULEURS_PIE[i % COULEURS_PIE.length] }}
                  />
                  <span>{d.nom}</span>
                  <span className="gold">{fmt$(d.valeur)}</span>
                  <span className="muted">{d.pct.toFixed(0)} %</span>
                  {nbUnites > 0 && <span className="muted">{fmt$(d.valeur / nbUnites)} / u</span>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comparaison aux batches precedents */}
      {batchPrecedents.length > 0 && (
        <section className="bap-section">
          <h4 className="bap-section-titre">Comparaison aux batches precedents</h4>
          <div className="bap-chart-container card">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={dataComparaison}
                margin={{ left: 10, right: 20, top: 10, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="nom"
                  tick={{ fill: 'var(--muted)', fontSize: 10 }}
                  angle={-25}
                  textAnchor="end"
                  height={50}
                />
                <YAxis yAxisId="left"  unit="%" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                <YAxis yAxisId="right" unit="$" orientation="right" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 12,
                  }}
                  formatter={(v, name) => [name.includes('$') ? fmt$(v) : fmtPct(v), name]}
                />
                <Legend wrapperStyle={{ color: 'var(--muted)', fontSize: 11 }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="yield_global_pct"
                  name="Yield (%)"
                  stroke="var(--info)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="marge_brute_pct"
                  name="Marge brute (%)"
                  stroke="var(--accent)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cout_par_unite"
                  name="Cout/unite ($)"
                  stroke="var(--success)"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Prix de vente recommande */}
      {coutParUnite > 0 && suggestions.length > 0 && (
        <section className="bap-section">
          <h4 className="bap-section-titre">Prix de vente recommande</h4>
          <div className="card bap-prix-card">
            <div className="bap-prix-info">
              <span className="muted">Cout de revient :</span>
              <strong>{fmt$(coutParUnite)} / unite</strong>
            </div>
            {prixVenteDefaut > 0 && margeActuelle !== null && (
              <div className="bap-prix-actuel">
                <span className="muted">Prix actuel ({fmt$(prixVenteDefaut)}) :</span>
                <strong className={margeActuelle >= 45 ? 'success' : margeActuelle >= 35 ? 'warning' : 'danger'}>
                  {fmtPct(margeActuelle)} de marge
                </strong>
                {margeActuelle < 50 && (
                  <span className="bap-suggestion-msg">
                    <Icon name="attention" size={13} />
                    Pour atteindre 50 %, suggerer {fmt$(coutParUnite / 0.5)}
                  </span>
                )}
              </div>
            )}
            <table className="esc-table bap-prix-table">
              <thead>
                <tr>
                  <th>Marge cible</th>
                  <th>Prix suggere</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map(s => (
                  <tr key={s.marge}>
                    <td>{s.marge} %</td>
                    <td className={prixVenteDefaut > 0 && Math.abs(s.prix - prixVenteDefaut) < 0.01 ? 'gold' : ''}>
                      <strong>{fmt$(s.prix)}</strong>
                      {prixVenteDefaut > 0 && Math.abs(s.prix - prixVenteDefaut) < 0.50 ? ' — Prix actuel' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Etat vide si pas de données pertinentes */}
      {!coutParUnite && !batchPrecedents.length && (
        <div className="empty-state">
          <Icon name="analytics" size={32} />
          <p className="empty-state__titre">Données insuffisantes</p>
          <p>Finalisez le batch et configurez les prix des matières pour voir l'analytique.</p>
        </div>
      )}
    </div>
  );
}
